/**
 * Suppressions — the do-not-contact list.
 *
 * *** WHY THERE ARE ONLY TWO REASONS IN THE DROPDOWN ***
 * Staff may write `admin` or `abuse_report`. `bounce`, `complaint`, `opt_out` and `stop` are 400
 * BY DESIGN, and are not offered here at all — not disabled, not present. They are provenance
 * claims: "a provider webhook told us this address is dead", "the person themselves asked us to
 * stop". Staff have no standing to make either claim, and a dropdown containing them would be an
 * invitation to record an administrator's decision as the recipient's own words. The 400 is the
 * rail holding a line; if you hit it, the UI is wrong, not the API.
 *
 * *** WHY THE LIST HAS NO ADDRESSES IN IT ***
 * Addresses are stored as a keyed one-way hash, so they cannot be listed — each row carries only
 * a short hash prefix as a correlation handle. That is why "check" exists: it is the only way to
 * interrogate the list by address, because the server re-derives the hash from an address you
 * already hold. A list of everyone who asked not to be contacted would be a mailing list.
 *
 * Removal re-opens contact with someone who asked to be left alone. It is a POST with the address
 * in the body, never a DELETE with it in the path — an email in a URL lands in access logs,
 * referrers and browser history, which is a poor place for exactly this address.
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  STAFF_SUPPRESSION_REASONS,
  addSuppression,
  checkSuppression,
  fetchSuppressions,
  removeSuppression,
  type StaffSuppressionReason,
  type SuppressionChannel,
} from '@/lib/admin-api';
import { formatTimestamp } from '@/lib/wire';
import { PageHeader } from '@/components/AppShell';
import { ConfirmAction } from '@/components/ConfirmAction';
import { EmptyState, ErrorState, ErrorText, Loading } from '@/components/DataState';
import { Pager } from '@/components/Pager';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SelectField, TextField } from '@/components/ui/field';

type CheckResult = { address: string; suppressed: boolean } | null;

export function Suppressions() {
  const [offset, setOffset] = useState(0);
  const [channelFilter, setChannelFilter] = useState<SuppressionChannel | ''>('');
  const queryClient = useQueryClient();

  const suppressions = useQuery({
    queryKey: ['suppressions', offset, channelFilter],
    queryFn: () => fetchSuppressions(offset, channelFilter || undefined),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['suppressions'] });

  return (
    <>
      <PageHeader title="Suppressions">
        The do-not-contact list. Addresses are stored as a one-way hash and cannot be listed &mdash;
        use the check below to ask about a specific one.
      </PageHeader>

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <CheckPanel />
        <AddPanel onAdded={invalidate} />
      </div>

      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <h2 className="font-display text-[15px] font-semibold text-ink">Suppressed addresses</h2>
        <div className="w-[12rem]">
          <SelectField
            label="Channel"
            value={channelFilter}
            onChange={(event) => {
              setChannelFilter(event.target.value as SuppressionChannel | '');
              setOffset(0);
            }}
          >
            <option value="">All</option>
            <option value="email">Email</option>
            <option value="phone">Phone</option>
          </SelectField>
        </div>
      </div>

      {suppressions.isPending && <Loading label="Loading suppressions…" />}
      {suppressions.isError && <ErrorState onRetry={() => suppressions.refetch()} />}

      {suppressions.isSuccess &&
        (suppressions.data.items.length === 0 ? (
          <EmptyState title="Nothing suppressed">
            No addresses are on the do-not-contact list for this filter.
          </EmptyState>
        ) : (
          <>
            <ul className="flex flex-col gap-2">
              {suppressions.data.items.map((item) => (
                <li
                  key={item.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="font-mono text-[12.5px] font-medium text-ink">
                      {item.addressHashPrefix}
                      <span className="text-ink-70">…</span>
                    </p>
                    <p className="mt-0.5 font-body text-[11px] text-ink-70">
                      Added {formatTimestamp(item.createdAt)}
                      {item.sourceDetail ? ` · ${item.sourceDetail}` : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <Badge tone="neutral">{item.channel}</Badge>
                    <Badge tone={item.reason === 'abuse_report' ? 'coral' : 'neutral'}>
                      {item.reason}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
            <Pager
              offset={offset}
              total={suppressions.data.total}
              count={suppressions.data.items.length}
              onOffsetChange={setOffset}
              noun="address"
            />
            {/* The hash prefix is a correlation handle, not something you can act on — removal
                needs the address itself, which only the check/remove panel can take. */}
            <p className="mt-3 max-w-[76ch] font-body text-[11.5px] leading-relaxed text-ink-70">
              To un-suppress an address you need the address itself: the stored hash cannot be
              reversed into one. Use the check panel above.
            </p>
          </>
        ))}
    </>
  );
}

function CheckPanel() {
  const [channel, setChannel] = useState<SuppressionChannel>('email');
  const [address, setAddress] = useState('');
  const [result, setResult] = useState<CheckResult>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const check = useMutation({
    mutationFn: () => checkSuppression(channel, address.trim()),
    onSuccess: (data) => {
      setResult({ address: address.trim(), suppressed: data.suppressed });
      setError(null);
    },
    onError: () => setError('Couldn’t check that. Try again in a moment.'),
  });

  const remove = useMutation({
    mutationFn: () => removeSuppression(channel, address.trim()),
    onSuccess: () => {
      setResult({ address: address.trim(), suppressed: false });
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['suppressions'] });
    },
    onError: (caught) => {
      // 404 = not on the list. A real answer, not a failure — the row may have been removed by
      // someone else between the check and the confirm.
      setError(
        caught instanceof ApiError && caught.status === 404
          ? 'That address is not on the list — nothing to remove.'
          : 'Couldn’t remove that. Try again in a moment.',
      );
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setResult(null);
    check.mutate();
  }

  return (
    <section className="rounded-lg border border-line bg-surface p-5">
      <h2 className="font-display text-[14px] font-semibold text-ink">Check an address</h2>
      <p className="mt-1 font-body text-[11.5px] leading-relaxed text-ink-70">
        Hashed exactly as the pre-send gate does. Read-only &mdash; no audit row.
      </p>

      <form onSubmit={onSubmit} className="mt-3 flex flex-col gap-3" noValidate>
        <SelectField
          label="Channel"
          value={channel}
          onChange={(event) => setChannel(event.target.value as SuppressionChannel)}
        >
          <option value="email">Email</option>
          <option value="phone">Phone</option>
        </SelectField>
        <TextField
          label="Address"
          value={address}
          onChange={(event) => {
            setAddress(event.target.value);
            setResult(null);
          }}
          autoComplete="off"
          required
        />
        <div>
          <Button type="submit" size="sm" disabled={check.isPending || address.trim() === ''}>
            {check.isPending ? 'Checking…' : 'Check'}
          </Button>
        </div>
      </form>

      {error && <ErrorText className="mt-3">{error}</ErrorText>}

      {result && (
        <div className="mt-3 rounded-md border border-line-2 bg-paper-2 p-3.5">
          <p role="status" className="font-body text-[12.5px] font-medium text-ink">
            {result.suppressed ? 'On the do-not-contact list.' : 'Not on the list.'}
          </p>
          {result.suppressed && (
            <div className="mt-2.5">
              <ConfirmAction
                label="Un-suppress"
                triggerVariant="coral"
                title="Start contacting this address again?"
                confirmLabel="Yes — un-suppress"
                confirmVariant="coral"
                pending={remove.isPending}
                onConfirm={() => remove.mutate()}
              >
                <p>
                  This re-opens contact with someone who is on the list because they bounced,
                  complained, asked us to stop, or was placed here by us. Unless you know why it was
                  added and that the reason no longer holds, leave it.
                </p>
                <p className="mt-1.5">This is recorded against your staff id.</p>
              </ConfirmAction>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function AddPanel({ onAdded }: { onAdded: () => void }) {
  const [channel, setChannel] = useState<SuppressionChannel>('email');
  const [address, setAddress] = useState('');
  const [reason, setReason] = useState<StaffSuppressionReason>('admin');
  const [sourceDetail, setSourceDetail] = useState('');
  const [outcome, setOutcome] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () =>
      addSuppression({ channel, address: address.trim(), reason, sourceDetail: sourceDetail }),
    onSuccess: (data) => {
      setOutcome(
        data.status === 'already_suppressed'
          ? 'Already on the list — nothing changed.'
          : 'Added to the do-not-contact list.',
      );
      setError(null);
      setAddress('');
      setSourceDetail('');
      onAdded();
    },
    onError: () => setError('Couldn’t add that. Try again in a moment.'),
  });

  const selected = STAFF_SUPPRESSION_REASONS.find((option) => option.value === reason);

  return (
    <section className="rounded-lg border border-line bg-surface p-5">
      <h2 className="font-display text-[14px] font-semibold text-ink">Suppress an address</h2>
      <p className="mt-1 font-body text-[11.5px] leading-relaxed text-ink-70">
        Stops all outreach to it. Idempotent.
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          setOutcome(null);
          add.mutate();
        }}
        className="mt-3 flex flex-col gap-3"
        noValidate
      >
        <SelectField
          label="Channel"
          value={channel}
          onChange={(event) => setChannel(event.target.value as SuppressionChannel)}
        >
          <option value="email">Email</option>
          <option value="phone">Phone</option>
        </SelectField>
        <TextField
          label="Address"
          value={address}
          onChange={(event) => setAddress(event.target.value)}
          autoComplete="off"
          required
        />
        {/* Only the two reasons staff may assert. The provider- and recipient-originated ones
            are absent on purpose — see the module comment. */}
        <SelectField
          label="Reason"
          hint={selected?.detail}
          value={reason}
          onChange={(event) => setReason(event.target.value as StaffSuppressionReason)}
        >
          {STAFF_SUPPRESSION_REASONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectField>
        <TextField
          label="Source detail (optional)"
          hint="A short provenance note stored on the row. No personal details."
          value={sourceDetail}
          onChange={(event) => setSourceDetail(event.target.value)}
          autoComplete="off"
        />
        <div>
          <Button
            type="submit"
            variant="coral"
            size="sm"
            disabled={add.isPending || address.trim() === ''}
          >
            {add.isPending ? 'Adding…' : 'Suppress'}
          </Button>
        </div>
      </form>

      {error && <ErrorText className="mt-3">{error}</ErrorText>}
      {/* sage-deep clears AA here at 4.85:1, but ONLY because this sits on `surface`. It drops to
          4.51 on paper and 4.07 on sage-tint — so if this message ever moves onto a tint, it must
          become `text-ink` like the queue's and dead-letters' confirmations. */}
      {outcome && (
        <p role="status" className="mt-3 text-[12.5px] font-medium text-sage-text">
          {outcome}
        </p>
      )}
    </section>
  );
}
